/**
 * Le document en pages continues, sans ascenseur a lui (porteur,
 * 06/10/2026 : « arreter les ascenseurs et lister les pages »). Chaque page
 * porte son propre calque, cale sur sa taille rendue : l'editeur y pose
 * ses cadres en fraction de page, comme dans la visionneuse page a page.
 *
 * Les pages ne sont dessinees qu'a l'approche de l'ecran : un document de
 * 80 pages ne doit pas peindre 80 toiles d'un coup.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

// Import pour son EFFET : PdfViewer configure le worker de PDF.js
// (GlobalWorkerOptions.workerSrc). Un import de type seul est efface a la
// compilation - l'ecran plantait alors au premier getDocument (06/10/2026).
import "./PdfViewer";
import type { TaillePage } from "./PdfViewer";

interface Props {
  data: ArrayBuffer;
  /** 1 = largeur de la colonne. */
  zoom: number;
  calque?: (taille: TaillePage) => ReactNode;
  onPages?: (nombre: number) => void;
  onEchec?: () => void;
}

export function DocumentEnFlux({ data, zoom, calque, onPages, onEchec }: Props) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const conteneur = useRef<HTMLDivElement>(null);
  const [largeur, setLargeur] = useState(0);

  useEffect(() => {
    let annule = false;
    // Copie : PDF.js neutralise le tampon qu'on lui confie.
    let tache: ReturnType<typeof pdfjs.getDocument>;
    try {
      tache = pdfjs.getDocument({ data: data.slice(0) });
    } catch {
      onEchec?.();
      return;
    }
    tache.promise.then(
      (d) => {
        if (annule) {
          void d.destroy();
          return;
        }
        setDoc(d);
        onPages?.(d.numPages);
      },
      () => {
        if (!annule) onEchec?.();
      },
    );
    return () => {
      annule = true;
      void tache.destroy();
      setDoc(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const el = conteneur.current;
    if (!el) return;
    // Mesure immediate : un ResizeObserver ne previent pas dans un onglet
    // masque, et le document restait vide jusqu'au premier redimensionnement.
    setLargeur(Math.floor(el.clientWidth));
    if (typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(() => {
      const w = Math.floor(el.clientWidth);
      setLargeur((l) => (Math.abs(l - w) < 1 ? l : w));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // 100 % = une page lisible (900 px au plus), pas toute la colonne : sur
  // grand ecran une page A4 de 1 200 px de large se lisait mal.
  const w = Math.floor(Math.min(largeur, 900 * zoom, largeur * zoom));
  return (
    <div className="flux" ref={conteneur}>
      {doc && w > 0 && Array.from({ length: doc.numPages }, (_, i) => (
        <PageEnFlux key={i} doc={doc} n={i + 1} largeur={w} calque={calque} />
      ))}
    </div>
  );
}

function PageEnFlux({ doc, n, largeur, calque }: {
  doc: PDFDocumentProxy;
  n: number;
  largeur: number;
  calque?: (taille: TaillePage) => ReactNode;
}) {
  const bloc = useRef<HTMLDivElement>(null);
  const toile = useRef<HTMLCanvasElement>(null);
  const [ratio, setRatio] = useState(1.414);
  const [proche, setProche] = useState(n <= 2);

  useEffect(() => {
    let annule = false;
    void doc.getPage(n).then((p) => {
      const vp = p.getViewport({ scale: 1 });
      if (!annule) setRatio(vp.height / vp.width);
    });
    return () => {
      annule = true;
    };
  }, [doc, n]);

  useEffect(() => {
    const el = bloc.current;
    if (!el || proche) return;
    const obs = new IntersectionObserver((e) => {
      if (e.some((x) => x.isIntersecting)) setProche(true);
    }, { rootMargin: "1200px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [proche]);

  useEffect(() => {
    if (!proche) return;
    let annule = false;
    let rendu: { cancel: () => void } | null = null;
    void (async () => {
      const p = await doc.getPage(n);
      const c = toile.current;
      if (annule || !c) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vp = p.getViewport({ scale: (largeur / p.getViewport({ scale: 1 }).width) * dpr });
      c.width = vp.width;
      c.height = vp.height;
      const t = p.render({ canvasContext: c.getContext("2d")!, viewport: vp });
      rendu = t;
      try {
        await t.promise;
      } catch {
        // Rendu annule (zoom change) : pas une erreur.
      }
    })();
    return () => {
      annule = true;
      rendu?.cancel();
    };
  }, [doc, n, largeur, proche]);

  const hauteur = Math.round(largeur * ratio);
  return (
    <div className="flux__page" ref={bloc} style={{ width: largeur, height: hauteur }}>
      <canvas ref={toile} className="flux__toile" style={{ width: largeur, height: hauteur }} />
      <span className="flux__numero">{n}</span>
      {calque && <div className="viewer__calque">{calque({ page: n, largeur, hauteur })}</div>}
    </div>
  );
}
